import clsx from 'clsx';
import { SegmentedControlOption } from 'components/inputs/segmented-control-types';

type SegmentedControlProps<T extends string> = {
  label: string;
  options: SegmentedControlOption<T>[];
  value: T | null;
  onChange: (value: T) => void;
  disabled?: boolean;
  className?: string;
};

export default function SegmentedControl<T extends string>({
  label,
  options,
  value,
  onChange,
  disabled,
  className
}: SegmentedControlProps<T>) {
  const handleSelect = (option: SegmentedControlOption<T>) => {
    if (option.value !== value) {
      onChange(option.value);
    }
  };

  return (
    <div
      role='radiogroup'
      aria-label={label}
      className={clsx(
        'inline-flex flex-none overflow-hidden rounded-full border border-gray-300 bg-white',
        disabled && 'opacity-70',
        className
      )}
    >
      {options.map((option, index) => {
        const selected = value === option.value;
        return (
          <button
            key={option.value}
            type='button'
            role='radio'
            aria-checked={selected}
            disabled={disabled}
            onClick={() => handleSelect(option)}
            className={clsx(
              'whitespace-nowrap px-3 py-1 text-sm font-semibold transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-500',
              index > 0 && 'border-l border-gray-300',
              selected ? 'bg-blue-600 text-white' : 'bg-white text-gray-700',
              disabled ? 'cursor-not-allowed' : 'cursor-pointer',
              !disabled && !selected && 'hover:text-blue-600'
            )}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}
